import { defineStore } from 'pinia'
import { ref } from 'vue'

const COLLAPSED_KEY = 'bs2.v2.sidebarCollapsed'
const SECTIONS_KEY = 'bs2.v2.sidebarSections'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

function readSections(): string[] {
  try {
    const raw = localStorage.getItem(SECTIONS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : []
  } catch {
    return []
  }
}

/**
 * État de la sidebar V2 (cf design-system/MASTER.md §10).
 * `collapsed` (desktop) et `expandedSections` sont persistés en localStorage ;
 * `mobileOpen` pilote le <dialog> mobile et n'est jamais persisté.
 */
export const useSidebarStore = defineStore('v2Sidebar', () => {
  const collapsed = ref(readCollapsed())
  const mobileOpen = ref(false)
  const expandedSections = ref<string[]>(readSections())

  function persistCollapsed() {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed.value ? '1' : '0')
    } catch {
      // localStorage indisponible (mode privé strict) — on ignore
    }
  }

  function persistSections() {
    try {
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(expandedSections.value))
    } catch {
      // localStorage indisponible (mode privé strict) — on ignore
    }
  }

  function setCollapsed(value: boolean) {
    collapsed.value = value
    persistCollapsed()
  }

  function toggleCollapsed() {
    setCollapsed(!collapsed.value)
  }

  function isSectionExpanded(id: string): boolean {
    return expandedSections.value.includes(id)
  }

  function expandSection(id: string) {
    if (!expandedSections.value.includes(id)) {
      expandedSections.value = [...expandedSections.value, id]
      persistSections()
    }
  }

  function toggleSection(id: string) {
    expandedSections.value = expandedSections.value.includes(id)
      ? expandedSections.value.filter((s) => s !== id)
      : [...expandedSections.value, id]
    persistSections()
  }

  function openMobile() {
    mobileOpen.value = true
  }

  function closeMobile() {
    mobileOpen.value = false
  }

  return {
    collapsed,
    mobileOpen,
    expandedSections,
    setCollapsed,
    toggleCollapsed,
    isSectionExpanded,
    expandSection,
    toggleSection,
    openMobile,
    closeMobile,
  }
})
